/**
 * ConfigurationPriorityInfo Component
 *
 * Explains the hierarchy of configuration overrides to users.
 * Shows priority from strongest (user email) to weakest (default status).
 */

var React = require('react');
var useState = React.useState;

/**
 * ConfigurationPriorityInfo component
 * Collapsible info section explaining configuration priority
 */
var ConfigurationPriorityInfo = function () {
  var expandedState = useState(false);
  var isExpanded = expandedState[0];
  var setIsExpanded = expandedState[1];

  var handleToggle = function () {
    setIsExpanded(!isExpanded);
  };

  // Priority levels from strongest to weakest
  var priorities = [
    {
      level: 1,
      name: 'User Email',
      description: 'Overrides for specific users by email address',
      icon: '👤',
      color: '#7c3aed',
    },
    {
      level: 2,
      name: 'Farming Unit',
      description: 'Overrides for specific farming units',
      icon: '🌾',
      color: '#059669',
    },
    {
      level: 3,
      name: 'Distribution',
      description: 'Overrides for entire distributions',
      icon: '📦',
      color: '#0891b2',
    },
    {
      level: 4,
      name: 'Environment',
      description: 'Overrides per environment (prod, staging, dev)',
      icon: '🌍',
      color: '#d97706',
    },
    {
      level: 5,
      name: 'Default Status',
      description: 'Fallback value when no overrides match',
      icon: '⚙️',
      color: '#6b7280',
    },
  ];

  return React.createElement(
    'div',
    {
      className: 'remote-config-priority-info',
    },
    [
      // Toggle button
      React.createElement(
        'button',
        {
          key: 'toggle',
          className: 'remote-config-priority-info__toggle',
          onClick: handleToggle,
          'aria-expanded': isExpanded,
        },
        [
          React.createElement(
            'svg',
            {
              key: 'icon',
              width: 18,
              height: 18,
              viewBox: '0 0 24 24',
              fill: 'none',
              stroke: 'currentColor',
              strokeWidth: 2,
              strokeLinecap: 'round',
              strokeLinejoin: 'round',
            },
            [
              React.createElement('circle', {
                key: 'c',
                cx: 12,
                cy: 12,
                r: 10,
              }),
              React.createElement('line', {
                key: 'l1',
                x1: 12,
                y1: 16,
                x2: 12,
                y2: 12,
              }),
              React.createElement('line', {
                key: 'l2',
                x1: 12,
                y1: 8,
                x2: 12.01,
                y2: 8,
              }),
            ],
          ),
          React.createElement(
            'span',
            { key: 'text' },
            'How configuration priority works',
          ),
          React.createElement(
            'svg',
            {
              key: 'chevron',
              className:
                'remote-config-priority-info__chevron' +
                (isExpanded
                  ? ' remote-config-priority-info__chevron--expanded'
                  : ''),
              width: 16,
              height: 16,
              viewBox: '0 0 24 24',
              fill: 'none',
              stroke: 'currentColor',
              strokeWidth: 2,
            },
            React.createElement('polyline', { points: '6 9 12 15 18 9' }),
          ),
        ],
      ),

      // Expanded content
      isExpanded &&
        React.createElement(
          'div',
          {
            key: 'content',
            className: 'remote-config-priority-info__content',
          },
          [
            React.createElement(
              'p',
              {
                key: 'intro',
                className: 'remote-config-priority-info__intro',
              },
              'When determining which value to use for a configuration, the system checks overrides in order of priority. The first matching override wins.',
            ),

            // Priority diagram
            React.createElement(
              'div',
              {
                key: 'diagram',
                className: 'remote-config-priority-info__diagram',
              },
              [
                // Arrow showing direction
                React.createElement(
                  'div',
                  {
                    key: 'arrow-label',
                    className: 'remote-config-priority-info__arrow-label',
                  },
                  [
                    React.createElement('span', { key: 'strong' }, 'Strongest'),
                    React.createElement('div', {
                      key: 'arrow',
                      className: 'remote-config-priority-info__arrow',
                    }),
                    React.createElement('span', { key: 'weak' }, 'Weakest'),
                  ],
                ),

                // Priority items
                React.createElement(
                  'div',
                  {
                    key: 'items',
                    className: 'remote-config-priority-info__items',
                  },
                  priorities.map(function (priority, index) {
                    return React.createElement(
                      'div',
                      {
                        key: priority.level,
                        className: 'remote-config-priority-info__item',
                      },
                      [
                        React.createElement(
                          'div',
                          {
                            key: 'badge',
                            className: 'remote-config-priority-info__badge',
                            style: { backgroundColor: priority.color },
                          },
                          priority.level,
                        ),
                        React.createElement(
                          'div',
                          {
                            key: 'content',
                            className:
                              'remote-config-priority-info__item-content',
                          },
                          [
                            React.createElement(
                              'div',
                              {
                                key: 'name',
                                className:
                                  'remote-config-priority-info__item-name',
                              },
                              [
                                React.createElement(
                                  'span',
                                  {
                                    key: 'icon',
                                    style: { marginRight: '6px' },
                                  },
                                  priority.icon,
                                ),
                                priority.name,
                              ],
                            ),
                            React.createElement(
                              'div',
                              {
                                key: 'desc',
                                className:
                                  'remote-config-priority-info__item-desc',
                              },
                              priority.description,
                            ),
                          ],
                        ),
                        index < priorities.length - 1 &&
                          React.createElement('div', {
                            key: 'connector',
                            className: 'remote-config-priority-info__connector',
                          }),
                      ],
                    );
                  }),
                ),
              ],
            ),

            // Example
            React.createElement(
              'div',
              {
                key: 'example',
                className: 'remote-config-priority-info__example',
              },
              [
                React.createElement(
                  'div',
                  {
                    key: 'title',
                    className: 'remote-config-priority-info__example-title',
                  },
                  'Example',
                ),
                React.createElement(
                  'p',
                  {
                    key: 'text',
                    className: 'remote-config-priority-info__example-text',
                  },
                  'If a feature has Default Status = OFF, but Environment "prod" = ON, and User "john@example.com" = OFF, then John will see the feature as OFF (user override wins), while other production users will see it as ON.',
                ),
              ],
            ),
          ],
        ),
    ],
  );
};

module.exports = ConfigurationPriorityInfo;
